import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

function TicketRating() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithApprovalToken, isAuthenticated } = useAuth();
  const authAttemptedRef = useRef(false);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [authenticating, setAuthenticating] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const approvalToken = params.get('approval_token');
    const approvalTicketId = params.get('ticket_id');

    if (!approvalToken || authAttemptedRef.current || isAuthenticated) {
      return;
    }

    authAttemptedRef.current = true;
    setAuthenticating(true);
    loginWithApprovalToken(approvalToken, approvalTicketId || token || 'rating')
      .catch(err => {
        console.error('Auto login via approval token failed:', err);
      })
      .finally(() => setAuthenticating(false));
  }, [location.search, loginWithApprovalToken, isAuthenticated, token]);

  useEffect(() => {
    fetchTicketDetails();
  }, [token]);

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`helpdesk/rate/${token}`);
      
      if (response.data.status === 1) {
        setTicketDetails(response.data.data);
      } else {
        setError(response.data.message || 'Invalid or expired rating link');
      }
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      setError(err.response?.data?.message || 'This rating link is invalid or has already been used');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.post(`helpdesk/rate`, {
        token,
        rating,
        comment: comment.trim()
      });

      if (response.data.status === 1) {
        setSubmitted(true);
        toast.success('Thank you for your feedback!');
      } else {
        toast.error(response.data.message || 'Failed to submit rating');
      }
    } catch (err) {
      console.error('Error submitting rating:', err);
      toast.error(err.response?.data?.message || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || authenticating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 mb-4" style={{ borderColor: 'var(--steel-blue)' }}></div>
          <p className="text-gray-600">{authenticating ? 'Authenticating...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 text-white rounded-lg shadow-sm hover:shadow-md transition-all"
            style={{ backgroundColor: 'var(--steel-blue)' }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-2">Your feedback has been submitted successfully.</p>
          <p className="text-sm text-gray-500 mb-6">We appreciate you taking the time to rate your support experience.</p>
          <div className="flex items-center justify-center mb-6">
            <div className="text-4xl font-bold" style={{ color: 'var(--steel-blue)' }}>{rating}/10</div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 text-white rounded-lg shadow-sm hover:shadow-md transition-all"
            style={{ backgroundColor: 'var(--steel-blue)' }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-8 py-6 text-center" style={{ background: 'linear-gradient(135deg, var(--steel-blue), var(--picton-blue))' }}>
          <h1 className="text-2xl font-bold text-white mb-2">Rate Your Support Experience</h1>
          <p className="text-sm text-white opacity-90">Help us improve our service</p>
        </div>

        {/* Ticket Info */}
        {ticketDetails && (
          <div className="px-8 py-6 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ticket Number</p>
                <p className="text-lg font-semibold text-gray-900">#{ticketDetails.ticket_number}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Subject</p>
                <p className="text-lg font-semibold text-gray-900">{ticketDetails.title}</p>
              </div>
            </div>
          </div>
        )}

        {/* Rating Form */}
        <form onSubmit={handleSubmit} className="px-8 py-8">
          {/* Rating Scale */}
          <div className="mb-8">
            <label className="block text-lg font-semibold text-gray-900 mb-4 text-center">
              How would you rate your support experience?
            </label>
            <div className="flex justify-center items-center gap-2 mb-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRating(value)}
                  onMouseEnter={() => setHoveredRating(value)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className={`w-12 h-12 rounded-lg font-bold text-lg transition-all transform hover:scale-110 ${
                    value <= (hoveredRating || rating)
                      ? 'text-white shadow-lg'
                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                  style={{
                    backgroundColor: value <= (hoveredRating || rating) 
                      ? value <= 3 
                        ? '#ef4444' 
                        : value <= 6 
                          ? '#f59e0b' 
                          : '#10b981'
                      : undefined
                  }}
                >
                  {value}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-sm text-gray-500 px-2">
              <span>Poor</span>
              <span>Excellent</span>
            </div>
            {rating > 0 && (
              <div className="text-center mt-4">
                <p className="text-2xl font-bold" style={{ 
                  color: rating <= 3 ? '#ef4444' : rating <= 6 ? '#f59e0b' : '#10b981'
                }}>
                  {rating}/10
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {rating <= 3 && 'We\'re sorry to hear that. We\'ll work to improve.'}
                  {rating > 3 && rating <= 6 && 'Thank you for your feedback. We\'ll strive to do better.'}
                  {rating > 6 && rating <= 8 && 'Great! We\'re glad we could help.'}
                  {rating > 8 && 'Excellent! We\'re thrilled with your experience!'}
                </p>
              </div>
            )}
          </div>

          {/* Comment */}
          <div className="mb-8">
            <label htmlFor="comment" className="block text-sm font-semibold text-gray-900 mb-2">
              Additional Comments (Optional)
            </label>
            <textarea
              id="comment"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent resize-none"
              style={{ '--tw-ring-color': 'var(--steel-blue)' }}
              placeholder="Tell us more about your experience..."
            />
            <p className="text-xs text-gray-500 mt-2">
              Your feedback helps us improve our support service
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={submitting || rating === 0}
              className="px-8 py-3 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              style={{ backgroundColor: 'var(--steel-blue)' }}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Submit Rating
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TicketRating;

